import { create } from "zustand";
import { syncTabs } from "zustand-sync-tabs";
import { devtools } from "zustand/middleware";
import { MaintenanceMode } from "ts/api/constraints";
import { useNetworkStatus } from "store/network-status/network-status.store";
import { isEqual, isPlainObject } from "lodash";

type State = {
  panelHeight: number;
  maintenanceMode: MaintenanceMode;
};

type Action = {
  updatePanelHeight: (height: number) => void;
  autoCatch: () => () => void;
  shouldShowMaintenanceModeNotification: () => boolean;
};

const MAINTENANCE_URL = import.meta.env.VITE_MAINTENANCE_URL;
const REFRESH_INTERVAL = 60_000;
const FETCH_TIMEOUT = 10_000;

const DEFAULT_MAINTENANCE_MODE: MaintenanceMode = {
  enabled: false,
  description: "",
  showNotification: false,
};

const parseDescriptionTranslations = (
  value: unknown
): Record<string, string> | undefined => {
  if (!isPlainObject(value)) {
    return undefined;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string"
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
};

// Fail-open: anything that is not a well-formed file means "maintenance off".
const parseMaintenanceMode = (value: unknown): MaintenanceMode => {
  if (!isPlainObject(value)) {
    return DEFAULT_MAINTENANCE_MODE;
  }
  const {
    enabled,
    description,
    showNotification,
    descriptionTranslations,
  } = value as Record<string, unknown>;
  if (typeof enabled !== "boolean" || typeof description !== "string") {
    return DEFAULT_MAINTENANCE_MODE;
  }
  const translations = parseDescriptionTranslations(descriptionTranslations);
  return {
    enabled,
    description,
    showNotification: showNotification === true,
    ...(translations && { descriptionTranslations: translations }),
  };
};

const fetchMaintenanceMode = async (url: string): Promise<MaintenanceMode> => {
  try {
    const response = await fetch(url, {
      cache: "no-store",
      signal: AbortSignal.timeout(FETCH_TIMEOUT),
    });
    if (!response.ok) {
      return DEFAULT_MAINTENANCE_MODE;
    }
    return parseMaintenanceMode(await response.json());
  } catch {
    return DEFAULT_MAINTENANCE_MODE;
  }
};

export const useMaintenanceModeStore = create(
  devtools<State & Action>(
    syncTabs(
      (set, get) => ({
        panelHeight: 0,
        maintenanceMode: DEFAULT_MAINTENANCE_MODE,
        shouldShowMaintenanceModeNotification: () => {
          const { maintenanceMode } = get();
          return (
            !maintenanceMode?.enabled &&
            !!maintenanceMode?.description &&
            !!maintenanceMode?.showNotification
          );
        },
        updatePanelHeight: (panelHeight) => {
          set({ panelHeight });
        },
        autoCatch: () => {
          if (!MAINTENANCE_URL) {
            return () => undefined;
          }

          let latestRequestId = 0;
          const refresh = async () => {
            latestRequestId += 1;
            const requestId = latestRequestId;
            const mode = await fetchMaintenanceMode(MAINTENANCE_URL);
            if (
              requestId !== latestRequestId ||
              isEqual(get().maintenanceMode, mode)
            ) {
              return;
            }
            set({ maintenanceMode: mode });
          };

          const intervalId = window.setInterval(() => {
            if (document.visibilityState === "visible") {
              refresh();
            }
          }, REFRESH_INTERVAL);

          const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
              refresh();
            }
          };
          document.addEventListener("visibilitychange", onVisibilityChange);

          // API failures are the moment an admin is most likely to have just
          // switched maintenance on, so don't wait for the next interval.
          const unsubscribeNetworkStatus = useNetworkStatus.subscribe(
            (state, prevState) => {
              if (
                state.status !== prevState.status &&
                (state.status === "unstable" ||
                  state.status === "services-unavailable")
              ) {
                refresh();
              }
            }
          );

          refresh();

          return () => {
            latestRequestId += 1;
            window.clearInterval(intervalId);
            document.removeEventListener(
              "visibilitychange",
              onVisibilityChange
            );
            unsubscribeNetworkStatus();
          };
        },
      }),
      {
        name: "maintenance-mode",
      }
    )
  )
);
